//FailedWoDetailsModal.js

import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./FailedWoDetailsModal.css";
import { getAuthHeaders } from "../Auth/authHelpers";

const FailedWoDetailsModal = ({
	details,
	onClose,
	alias,
	coreCompCode,
	trainerID,
}) => {
	const API = process.env.REACT_APP_API_URL;
	const [showResponses, setShowResponses] = useState({});
	const [localDetails, setLocalDetails] = useState(details || []);
	const [questions, setQuestions] = useState({});
	const [appealsCount, setAppealsCount] = useState(0);
	const maxAppeals = 2;

	const authHeaders = useCallback(
		() => getAuthHeaders(trainerID),
		[trainerID],
	);

	useEffect(() => {
		setLocalDetails(Array.isArray(details) ? details : []);
	}, [details]);

	useEffect(() => {
		const fetchAppealsCount = async () => {
			if (!alias || !coreCompCode || !trainerID) return;

			try {
				const response = await fetch(
					`${API}/failed-wo/appeals-count/${encodeURIComponent(alias)}/${encodeURIComponent(coreCompCode)}`,
					{
						headers: await authHeaders(),
					},
				);

				if (response.ok) {
					const data = await response.json();
					setAppealsCount(Number(data.appealCount || 0));
				} else {
					console.error("Failed to fetch appeals count");
				}
			} catch (error) {
				console.error("Error fetching appeals count:", error);
			}
		};

		fetchAppealsCount();
	}, [API, alias, coreCompCode, authHeaders, trainerID]);

	useEffect(() => {
		const fetchQuestions = async () => {
			if (!Array.isArray(details) || details.length === 0) return;

			for (const detail of details) {
				if (!detail.template) continue;

				try {
					const encodedTemplateId = encodeURIComponent(detail.template);
					const response = await fetch(
						`${API}/templates/template-questions/${encodedTemplateId}`,
						{
							headers: await authHeaders(),
						},
					);

					if (!response.ok) {
						throw new Error("Network response was not ok");
					}

					const data = await response.json();

					if (Array.isArray(data) && data.length > 0) {
						setQuestions((prevQuestions) => ({
							...prevQuestions,
							[detail.wo_num]: data[0],
						}));
					}
				} catch (error) {
					console.error("Error fetching questions:", error);
				}
			}
		};

		fetchQuestions();
	}, [API, details, authHeaders]);

	const toggleResponses = (index) => {
		setShowResponses((prev) => ({ ...prev, [index]: !prev[index] }));
	};

	const renderQuestions = (woNum) => {
		const detailQuestions = questions[woNum];

		return detailQuestions ? (
			Object.entries(detailQuestions).map(([key, value], index) => (
				<p key={index}>
					<strong>{key.replaceAll("_", " ")}:</strong> {value}
				</p>
			))
		) : (
			<p>No questions available.</p>
		);
	};

	const formatDate = (dateString) => {
		return dateString ? new Date(dateString).toLocaleDateString() : "";
	};

	const handleAppeal = async (woNum, index) => {
		try {
			const response = await fetch(
				`${API}/failed-wo/appeal-grade/${encodeURIComponent(woNum)}`,
				{
					method: "PUT",
					headers: await authHeaders(),
				},
			);

			if (!response.ok) throw new Error("Failed to appeal grade");

			const updatedDetails = [...localDetails];
			updatedDetails[index].regrade_flag = "Yes";
			setLocalDetails(updatedDetails);
			setAppealsCount((prevCount) => prevCount + 1);

			alert("Grade appeal successful!");
		} catch (error) {
			console.error("Error appealing grade:", error);
			alert("Error processing grade appeal");
		}
	};

	const handleWithdrawAppeal = async (woNum, index) => {
		try {
			const response = await fetch(
				`${API}/failed-wo/withdraw-appeal/${encodeURIComponent(woNum)}`,
				{
					method: "PUT",
					headers: await authHeaders(),
				},
			);

			if (!response.ok) throw new Error("Failed to withdraw appeal");

			const updatedDetails = [...localDetails];
			updatedDetails[index].regrade_flag = null;
			setLocalDetails(updatedDetails);
			setAppealsCount((prevCount) => Math.max(prevCount - 1, 0));

			alert("Appeal withdrawn successfully!");
		} catch (error) {
			console.error("Error withdrawing appeal:", error);
			alert("Error processing appeal withdrawal");
		}
	};

	if (!localDetails || localDetails.length === 0) {
		return <p>No failed work order details available.</p>;
	}

	return (
		<div className="modal-backdrop">
			<div className="modal-content">
				<h2>Work Order Details</h2>
				<div className="modal-body">
					{localDetails.map((detail, index) => {
						const isDuplicate =
							String(detail.duplicate_wo || "").toLowerCase() === "true";

						return (
							<div
								key={`${detail.wo_num}-${index}`}
								className="detail-container"
							>
								<p>
									<strong>WO Number:</strong> {detail.wo_num}
								</p>
								<p>
									<strong>Completion Date:</strong>{" "}
									{detail.wo_comp_date
										? String(detail.wo_comp_date).split("T")[0]
										: ""}
								</p>
								<p>
									<strong>Template:</strong> {detail.template || "N/A"}
								</p>
								<p>
									<strong>Total Score:</strong> {detail.total_score}
								</p>

								{detail.regrade_flag === "Yes" && (
									<p className="appeal-pending">Appeal Pending</p>
								)}

								<div className="button-container">
									<button
										className="mr-4 show-responses"
										onClick={() => toggleResponses(index)}
									>
										{showResponses[index] ? "Hide Responses" : "Show Responses"}
									</button>

									{detail.final_status === "fail" ? (
										<>
											{isDuplicate ? (
												<button className="btn-disabled-gray" disabled>
													Duplicate - Cannot Appeal
												</button>
											) : detail.regrade_flag === "Yes" ? (
												<button
													className="withdraw btn-warning"
													onClick={() =>
														handleWithdrawAppeal(detail.wo_num, index)
													}
												>
													Withdraw Appeal
												</button>
											) : appealsCount >= maxAppeals ? (
												<button className="btn-disabled-gray" disabled>
													Max Appeals Reached
												</button>
											) : (
												<button
													className="btn-appeal"
													onClick={() => handleAppeal(detail.wo_num, index)}
												>
													Appeal Grade
												</button>
											)}
										</>
									) : (
										<button className="btn-disabled-gray" disabled>
											Passed - No Appeal
										</button>
									)}
								</div>

								{showResponses[index] && (
									<div className="mt-3">
										{renderQuestions(detail.wo_num)}
										<p>
											<strong>Response 1:</strong> {detail.response_1}
										</p>
										<p>
											<strong>Response 2:</strong> {detail.response_2}
										</p>
										<p>
											<strong>Response 3:</strong> {detail.response_3}
										</p>
										<p>
											<strong>Response 4:</strong> {detail.response_4}
										</p>
										<p>
											<strong>Response 5:</strong> {detail.response_5}
										</p>
										<p>
											<strong>Score 1:</strong> {detail.score_1}
										</p>
										<p>
											<strong>Score 2:</strong> {detail.score_2}
										</p>
										<p>
											<strong>Score 3:</strong> {detail.score_3}
										</p>
										<p>
											<strong>Score 4:</strong> {detail.score_4}
										</p>
										<p>
											<strong>Score 5:</strong> {detail.score_5}
										</p>
									</div>
								)}
							</div>
						);
					})}
				</div>
				<div className="modal-footer">
					<button onClick={onClose}>Close</button>
				</div>
			</div>
		</div>
	);
};

export default FailedWoDetailsModal;
